/**
 * Legacy Excel (`.xls`) reading, with no dependencies.
 *
 * Two layers: an OLE2 compound-file reader (the container `.xls` files are
 * wrapped in) and a BIFF record parser (the workbook itself). BIFF8 (Excel 97
 * and later) is the target; BIFF5/7 files still parse, since the only real
 * difference for cell values is how strings are stored.
 *
 * Output matches ./xlsx.js: `{ sheets: [{ name, rows }] }`, where a cell is a
 * string, a number, a `YYYY-MM-DD` string for date-formatted cells, or null.
 */
import { DATE_FORMAT_IDS, isDateFormat, serialToISO } from './sheet-values'

const u16 = (b, o) => b[o] | (b[o + 1] << 8)
const u32 = (b, o) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0

const OLE_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]

/** True when these bytes are an OLE2 compound file, which is what `.xls` is. */
export function isXls(bytes) {
  return OLE_SIGNATURE.every((b, i) => bytes[i] === b)
}

/* ------------------------------------------------------------------ */
/* OLE2 container                                                      */
/* ------------------------------------------------------------------ */

const FREE_SECTOR = 0xfffffffa // and everything above it: end-of-chain, FAT, DIFAT

function readOle(bytes) {
  if (!isXls(bytes)) throw Object.assign(new Error('Not an Excel file'), { code: 'sheet.not-excel' })
  const sectorSize = 1 << u16(bytes, 30)
  const miniSize = 1 << u16(bytes, 32)
  const dirStart = u32(bytes, 48)
  const miniCutoff = u32(bytes, 56)
  const miniFatStart = u32(bytes, 60)
  const difatStart = u32(bytes, 68)
  const difatCount = u32(bytes, 72)

  const fatSectors = []
  for (let i = 0; i < 109; i++) fatSectors.push(u32(bytes, 76 + i * 4))
  let next = difatStart
  for (let i = 0; i < difatCount && next < FREE_SECTOR; i++) {
    const base = 512 + next * sectorSize
    const perSector = sectorSize / 4 - 1
    for (let j = 0; j < perSector; j++) fatSectors.push(u32(bytes, base + j * 4))
    next = u32(bytes, base + perSector * 4)
  }

  const fat = []
  for (const sector of fatSectors) {
    if (sector >= FREE_SECTOR) continue
    const base = 512 + sector * sectorSize
    for (let i = 0; i < sectorSize / 4; i++) fat.push(u32(bytes, base + i * 4))
  }

  const chain = (start) => {
    const out = []
    for (let s = start; s < FREE_SECTOR && out.length <= fat.length; s = fat[s] ?? 0xffffffff) out.push(s)
    return out
  }
  const readChain = (start) => {
    const sectors = chain(start)
    const out = new Uint8Array(sectors.length * sectorSize)
    sectors.forEach((s, i) => out.set(bytes.subarray(512 + s * sectorSize, 512 + (s + 1) * sectorSize), i * sectorSize))
    return out
  }

  const dir = readChain(dirStart)
  const entries = []
  for (let off = 0; off + 128 <= dir.length; off += 128) {
    const nameLength = u16(dir, off + 64)
    let name = ''
    for (let i = 0; i + 1 < Math.max(nameLength - 2, 0); i += 2) name += String.fromCharCode(u16(dir, off + i))
    entries.push({ name, type: dir[off + 66], start: u32(dir, off + 116), size: u32(dir, off + 120) })
  }

  const root = entries.find((e) => e.type === 5)
  const miniStream = root ? readChain(root.start) : new Uint8Array(0)
  const miniFatRaw = readChain(miniFatStart)
  const miniFat = []
  for (let i = 0; i + 4 <= miniFatRaw.length; i += 4) miniFat.push(u32(miniFatRaw, i))

  const read = (entry) => {
    if (entry.size < miniCutoff) {
      const out = new Uint8Array(Math.ceil(entry.size / miniSize) * miniSize)
      let at = 0
      for (let s = entry.start; s < FREE_SECTOR && at < out.length; s = miniFat[s] ?? 0xffffffff) {
        out.set(miniStream.subarray(s * miniSize, (s + 1) * miniSize), at)
        at += miniSize
      }
      return out.subarray(0, entry.size)
    }
    return readChain(entry.start).subarray(0, entry.size)
  }

  const streams = new Map()
  for (const entry of entries) if (entry.type === 2) streams.set(entry.name, read(entry))
  return streams
}

/* ------------------------------------------------------------------ */
/* BIFF records                                                        */
/* ------------------------------------------------------------------ */

const REC = {
  FORMULA: 0x0006,
  EOF: 0x000a,
  CONTINUE: 0x003c,
  DATEMODE: 0x0022,
  FILEPASS: 0x002f,
  BLANK: 0x0201,
  NUMBER: 0x0203,
  LABEL: 0x0204,
  BOOLERR: 0x0205,
  STRING: 0x0207,
  BOUNDSHEET: 0x0085,
  MULRK: 0x00bd,
  MULBLANK: 0x00be,
  XF: 0x00e0,
  LABELSST: 0x00fd,
  SST: 0x00fc,
  RK: 0x027e,
  FORMAT: 0x041e,
  BOF: 0x0809,
}

function* records(stream) {
  let at = 0
  while (at + 4 <= stream.length) {
    const id = u16(stream, at)
    const length = u16(stream, at + 2)
    if (at + 4 + length > stream.length) return
    yield { id, at, data: stream.subarray(at + 4, at + 4 + length) }
    at += 4 + length
  }
}

const latin1 = (bytes) => {
  let out = ''
  for (const b of bytes) out += String.fromCharCode(b)
  return out
}
const utf16 = (bytes) => {
  let out = ''
  for (let i = 0; i + 1 < bytes.length; i += 2) out += String.fromCharCode(u16(bytes, i))
  return out
}

/** A BIFF8 string: 16-bit length, a flag byte, then 8- or 16-bit characters. */
function readUnicodeString(data, at, lengthBytes = 2) {
  const length = lengthBytes === 2 ? u16(data, at) : data[at]
  let p = at + lengthBytes
  const flags = data[p++]
  const runs = flags & 0x08 ? u16(data, (p += 2) - 2) : 0
  const extra = flags & 0x04 ? u32(data, (p += 4) - 4) : 0
  const wide = flags & 0x01
  const end = p + (wide ? length * 2 : length)
  const text = wide ? utf16(data.subarray(p, end)) : latin1(data.subarray(p, end))
  return { text, next: end + runs * 4 + extra }
}

/**
 * The shared-string table, which routinely spills across CONTINUE records —
 * and may switch between 8- and 16-bit characters mid-string when it does.
 */
function readSst(blocks) {
  const strings = []
  let bi = 0
  let p = 8 // total count, unique count
  const total = u32(blocks[0], 4)
  const nextBlock = () => (bi + 1 < blocks.length ? ((bi += 1), (p = 0), true) : false)

  while (strings.length < total) {
    if (p >= blocks[bi].length && !nextBlock()) break
    const block = blocks[bi]
    const length = u16(block, p)
    p += 2
    const flags = block[p++]
    const runs = flags & 0x08 ? ((p += 2), u16(block, p - 2)) : 0
    const extra = flags & 0x04 ? ((p += 4), u32(block, p - 4)) : 0
    let wide = flags & 0x01
    let text = ''
    let left = length
    while (left > 0) {
      const available = Math.floor((blocks[bi].length - p) / (wide ? 2 : 1))
      const take = Math.min(available, left)
      const slice = blocks[bi].subarray(p, p + (wide ? take * 2 : take))
      text += wide ? utf16(slice) : latin1(slice)
      p += wide ? take * 2 : take
      left -= take
      if (left > 0) {
        if (!nextBlock()) break
        wide = blocks[bi][p++] & 0x01 // each continuation restates the width
      }
    }
    p += runs * 4 + extra
    strings.push(text)
  }
  return strings
}

/** An RK value: a double squeezed into 30 bits, optionally scaled by 100. */
function decodeRk(raw) {
  const value =
    raw & 0x02
      ? (raw | 0) >> 2
      : (() => {
          const view = new DataView(new ArrayBuffer(8))
          view.setUint32(0, raw & 0xfffffffc)
          return view.getFloat64(0)
        })()
  return raw & 0x01 ? value / 100 : value
}

/**
 * @param {Uint8Array} bytes
 * @returns {{ sheets: { name: string, rows: (string|number|null)[][] }[] }}
 */
export function readXls(bytes) {
  const streams = readOle(bytes)
  const stream = streams.get('Workbook') ?? streams.get('Book')
  if (!stream) throw Object.assign(new Error('This file has no workbook in it'), { code: 'sheet.no-workbook' })

  const all = [...records(stream)]
  const byOffset = new Map(all.map((r, i) => [r.at, i]))

  let sst = []
  let date1904 = false
  const boundsheets = []
  const formats = new Map() // format index -> format string
  const xfFormat = [] // cell XF index -> format index

  for (let i = 0; i < all.length; i++) {
    const { id, data } = all[i]
    if (id === REC.EOF) break // globals substream ends before the first sheet
    switch (id) {
      case REC.FILEPASS:
        throw Object.assign(new Error('This file is password-protected'), { code: 'sheet.password' })
      case REC.DATEMODE:
        date1904 = u16(data, 0) === 1
        break
      case REC.BOUNDSHEET:
        boundsheets.push({
          at: u32(data, 0),
          hidden: (data[4] & 0x03) !== 0,
          kind: data[5],
          name: readUnicodeString(data, 6, 1).text,
        })
        break
      case REC.FORMAT:
        formats.set(u16(data, 0), readUnicodeString(data, 2).text)
        break
      case REC.XF:
        xfFormat.push(u16(data, 2))
        break
      case REC.SST: {
        const blocks = [data]
        while (all[i + 1]?.id === REC.CONTINUE) blocks.push(all[++i].data)
        sst = readSst(blocks)
        break
      }
    }
  }

  const dateXf = xfFormat.map((f) => (formats.has(f) ? isDateFormat(formats.get(f)) : DATE_FORMAT_IDS.has(f)))
  const isDateCell = (xf) => dateXf[xf] === true
  const numeric = (value, xf) => (isDateCell(xf) ? (serialToISO(value, date1904) ?? value) : value)

  const sheets = []
  for (const sheet of boundsheets) {
    if (sheet.kind !== 0x00) continue // chart / macro sheet
    const start = byOffset.get(sheet.at)
    if (start === undefined) continue

    const cells = new Map() // "row:col" -> value
    let maxRow = -1
    let maxCol = -1
    const put = (row, col, value) => {
      cells.set(`${row}:${col}`, value)
      if (row > maxRow) maxRow = row
      if (col > maxCol) maxCol = col
    }

    for (let i = start + 1; i < all.length; i++) {
      const { id, data } = all[i]
      if (id === REC.EOF) break
      if (id === REC.BOF) break
      const row = u16(data, 0)
      const col = u16(data, 2)
      switch (id) {
        case REC.LABELSST:
          put(row, col, sst[u32(data, 6)] ?? '')
          break
        case REC.LABEL:
          put(row, col, readUnicodeString(data, 6).text)
          break
        case REC.NUMBER:
          put(row, col, numeric(new DataView(data.buffer, data.byteOffset + 6, 8).getFloat64(0, true), u16(data, 4)))
          break
        case REC.RK:
          put(row, col, numeric(decodeRk(u32(data, 6)), u16(data, 4)))
          break
        case REC.MULRK:
          for (let c = col, p = 4; p + 6 <= data.length - 2; c++, p += 6) {
            put(row, c, numeric(decodeRk(u32(data, p + 2)), u16(data, p)))
          }
          break
        case REC.FORMULA: {
          // A formula caches its own result; a string result arrives next.
          const view = new DataView(data.buffer, data.byteOffset + 6, 8)
          if (u16(data, 12) === 0xffff) {
            if (data[6] === 0x00 && all[i + 1]?.id === REC.STRING) {
              put(row, col, readUnicodeString(all[++i].data, 0).text)
            } else if (data[6] === 0x01) {
              put(row, col, data[8] !== 0)
            }
          } else {
            put(row, col, numeric(view.getFloat64(0, true), u16(data, 4)))
          }
          break
        }
        case REC.BOOLERR:
          put(row, col, data[7] === 0 ? data[6] !== 0 : null)
          break
      }
    }

    const rows = []
    for (let r = 0; r <= maxRow; r++) {
      const line = new Array(maxCol + 1).fill(null)
      for (let c = 0; c <= maxCol; c++) line[c] = cells.get(`${r}:${c}`) ?? null
      rows.push(line)
    }
    sheets.push({ name: sheet.name, rows })
  }

  if (!sheets.length) throw Object.assign(new Error('This workbook has no worksheets'), { code: 'sheet.no-worksheets' })
  return { sheets }
}
